import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Barcode from "react-barcode";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSchoolName } from "./schoolNameStore";

/**
 * ═══════════════════════════════════════════════════════════════════════
 * FIXED: Barcode is visually SMALL but contains ALL data in FULL length
 * No truncation - all fields (Name, Father, Phone, Address, Blood, ID)
 * are encoded completely. The barcode appears small on the card but
 * when scanned, ALL details are shown.
 * ADDED: Logout button that navigates to /login page
 * ADDED: Rectangle card shape widened slightly and height reduced slightly
 * FIXED: Print and Download - exact live preview matching
 * FIXED: Mobile view responsive - all sections stack vertically on small screens
 * Desktop view remains unchanged
 * PREMIUM UI PASS: visual polish only (shadows, spacing, gradients, hover
 * states, photo sizing). No logic / state / API / handlers were changed.
 * ═══════════════════════════════════════════════════════════════════════
 */

const API_BASE = "https://schoolcrm-fzcv.onrender.com/api";

// ── Type configuration ──────────────────────────────────────────────────
const TYPE_CONFIG = {
  student: {
    label: "Student",
    icon: "fa-user-graduate",
    prefix: "STU",
    color: "#2C5EAD",
    gradient: "linear-gradient(135deg, #2C5EAD, #4BB8FA)",
    api: `${API_BASE}/admissions`,
    hasDb: true,
    hasParent: true,
  },
  staff: {
    label: "Staff",
    icon: "fa-chalkboard-teacher",
    prefix: "STF",
    color: "#1591DC",
    gradient: "linear-gradient(135deg, #1591DC, #4BB8FA)",
    api: `${API_BASE}/staff`,
    hasDb: true,
    hasParent: true,
  },
  worker: {
    label: "Worker",
    icon: "fa-user-hard-hat",
    prefix: "WRK",
    color: "#2C5EAD",
    gradient: "linear-gradient(135deg, #2C5EAD, #4BB8FA)",
    api: `${API_BASE}/workers`,
    hasDb: true,
    hasParent: false,
  },
  principal: {
    label: "Principal",
    icon: "fa-user-tie",
    prefix: "PRN",
    color: "#1591DC",
    gradient: "linear-gradient(135deg, #1591DC, #4BB8FA)",
    api: `${API_BASE}/headmistress`,
    hasDb: true,
    hasParent: false,
  },
  admin: {
    label: "Office Admin",
    icon: "fa-user-shield",
    prefix: "ADM",
    color: "#2C5EAD",
    gradient: "linear-gradient(135deg, #2C5EAD, #4BB8FA)",
    api: null,
    hasDb: false,
    hasParent: false,
  },
};

const SCHOOL_NAME_SUFFIX = "Group of Schools";
const SCHOOL_TAGLINE = "Govt. Recognised";
const SCHOOL_ADDRESS = "123, Anna Salai, Salem - 636001, Tamil Nadu";
const SCHOOL_PHONE = "+91 0123456789";

// ── Card shape presets ─────────────────────────────────────────────
const SHAPE_CONFIG = {
  rectangle: {
    label: "Rectangle",
    icon: "fa-rectangle-wide",
    w: 410,
    h: 280,
    smallW: 312,
    smallH: 220,
    radius: 16,
  },
  square: {
    label: "Square",
    icon: "fa-square",
    w: 300,
    h: 455,
    smallW: 224,
    smallH: 340,
    radius: 20,
  },
};

// ── Photo shape presets ────────────────────────────────────────────────
const PHOTO_SHAPE_CONFIG = {
  circle: { label: "Circle", icon: "fa-circle", radius: "50%", tall: false },
  square: { label: "Square", icon: "fa-square", radius: 12, tall: false },
  rectangle: { label: "Rectangle", icon: "fa-portrait", radius: 8, tall: true },
};

function getPhotoDims(photoShape, small) {
  const cfg = PHOTO_SHAPE_CONFIG[photoShape] || PHOTO_SHAPE_CONFIG.square;
  if (cfg.tall) {
    return { width: small ? 44 : 60, height: small ? 56 : 76, borderRadius: cfg.radius };
  }
  const size = small ? 50 : 70;
  return { width: size, height: size, borderRadius: cfg.radius };
}

const emptyForm = {
  sourceId: "",
  name: "",
  idNo: "",
  sub: "",
  phone: "",
  parentName: "",
  address: "",
  bloodGroup: "",
  validTill: "",
  session: "",
  photo: "",
};

// ── Manual card-color palette ────────────────────────────────────────────
const COLOR_PRESETS = [
  "#2C5EAD", "#1591DC", "#4BB8FA", "#C4E2F5", "#0D47A1", "#006994", "#1976D2", "#0288D1"
];

function lightenColor(hex, percent) {
  if (!hex) return hex;
  const clean = hex.replace("#", "");
  const num = parseInt(clean, 16);
  if (isNaN(num)) return hex;
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

function gradientFromColor(hex) {
  return `linear-gradient(135deg, ${hex}, ${lightenColor(hex, 35)})`;
}

function initials(name) {
  if (!name) return "?";
  return name.trim().split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function shortId(id) {
  if (!id) return Math.random().toString(36).slice(-6).toUpperCase();
  return String(id).slice(-6).toUpperCase();
}

// ═══════════════════════════════════════════════════════════════════════
// FIX: html2canvas can fire before base64 <img> tags (photo / signature)
// have actually finished decoding. When that happens it captures the
// browser's "broken image" placeholder instead of the real picture — that
// is the small black box people see near the barcode/signature area after
// download. This helper waits for every <img> inside a node to either
// finish loading or fail, so the snapshot always contains the real pixels.
// ═══════════════════════════════════════════════════════════════════════
function waitForImages(node) {
  if (!node) return Promise.resolve();
  const imgs = Array.from(node.querySelectorAll("img"));
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
        setTimeout(done, 3000);
      });
    })
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FIX: html2canvas can also fire before the 'Inter' Google Font has
// finished downloading/parsing. When that happens, the browser paints the
// text with its fallback font instead — which is why numbers/letters in
// the downloaded PDF sometimes look garbled or oddly styled compared to
// the live preview. Waiting on document.fonts.ready (and explicitly
// nudging Inter to load) guarantees the real font is used every time.
// ═══════════════════════════════════════════════════════════════════════
async function waitForFontsReady() {
  try {
    if (document.fonts) {
      await Promise.all([
        document.fonts.load('400 12px "Inter"'),
        document.fonts.load('600 12px "Inter"'),
        document.fonts.load('700 12px "Inter"'),
        document.fonts.load('800 12px "Inter"'),
        document.fonts.load('900 12px "Inter"'),
      ]).catch(() => { });
      await document.fonts.ready;
    }
  } catch {
    /* ignore — worst case we fall back to default timing */
  }
}

// ═══════════════════════════════════════════════════════════════════════
// FIXED BARCODE: Encodes ALL fields with FULL length (no truncation)
// Uses pipe separator between fields for easy parsing
// Format: N[Name]|F[Father]|P[Phone]|A[Address]|B[Blood]|I[ID]|T[Type]
// ═══════════════════════════════════════════════════════════════════════
function buildScanDetails(data) {
  let parts = [];

  if (data.name && data.name.trim()) {
    const name = String(data.name).replace(/[,|]/g, " ").trim();
    parts.push(`N${name}`);
  }
  if (data.parentName && data.parentName.trim()) {
    const parent = String(data.parentName).replace(/[,|]/g, " ").trim();
    parts.push(`F${parent}`);
  }
  if (data.phone && data.phone.trim()) {
    const phone = String(data.phone).replace(/[^0-9]/g, "");
    parts.push(`P${phone}`);
  }
  if (data.address && data.address.trim()) {
    const address = String(data.address).replace(/[,|]/g, " ").trim();
    parts.push(`A${address}`);
  }
  if (data.bloodGroup && data.bloodGroup.trim()) {
    const blood = String(data.bloodGroup).replace(/[,|]/g, " ").trim();
    parts.push(`B${blood}`);
  }
  if (data.idNo && data.idNo.trim()) {
    parts.push(`I${data.idNo}`);
  }
  if (data.type) {
    parts.push(`T${data.type}`);
  }

  return parts.join("|");
}

function decodeScanDetails(raw) {
  const result = {};
  const map = {
    N: "name",
    F: "parentName",
    P: "phone",
    A: "address",
    B: "bloodGroup",
    I: "idNo",
    T: "type"
  };
  const parts = String(raw || "").split("|");
  parts.forEach((part) => {
    if (!part) return;
    const key = map[part[0]];
    if (key) result[key] = part.slice(1);
  });
  return result;
}

function formatScannedData(raw) {
  const data = decodeScanDetails(raw);
  if (!data || Object.keys(data).length === 0) return "No data found";

  const lines = [];
  if (data.name) lines.push(`Name: ${data.name}`);
  if (data.parentName) lines.push(`Father/Parent: ${data.parentName}`);
  if (data.phone) lines.push(`Phone: ${data.phone}`);
  if (data.address) lines.push(`Address: ${data.address}`);
  if (data.bloodGroup) lines.push(`Blood Group: ${data.bloodGroup}`);
  if (data.idNo) lines.push(`ID: ${data.idNo}`);
  if (data.type) lines.push(`Type: ${data.type.toUpperCase()}`);

  return lines.join("\n");
}

function buildBarcodeScanData(data) {
  let parts = [];

  if (data.name && data.name.trim()) {
    parts.push(`N${String(data.name).replace(/[,|]/g, " ").trim()}`);
  }
  if (data.parentName && data.parentName.trim()) {
    parts.push(`F${String(data.parentName).replace(/[,|]/g, " ").trim()}`);
  }
  if (data.phone && data.phone.trim()) {
    parts.push(`P${String(data.phone).replace(/[^0-9]/g, "")}`);
  }
  if (data.address && data.address.trim()) {
    parts.push(`A${String(data.address).replace(/[,|]/g, " ").trim()}`);
  }
  if (data.bloodGroup && data.bloodGroup.trim()) {
    parts.push(`B${String(data.bloodGroup).replace(/[,|]/g, " ").trim()}`);
  }

  return parts.join("|");
}

function buildBarcodeApiUrl(value) {
  const encoded = encodeURIComponent(value);
  return `https://barcodeapi.org/api/128/${encoded}`;
}

function ScaledBarcode({ value, displayText, availablePx, barWidth, barHeight, fontSize }) {
  if (!value) return null;
  const boxWidth = Math.min(availablePx, 130);
  const height = barHeight || 12;
  const width = barWidth || 0.3;
  return (
    <div style={{ maxWidth: boxWidth, height: height, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Barcode
        value={value}
        width={width}
        height={height}
        displayValue={false}
        margin={0}
      />
    </div>
  );
}

function FieldRow({ label, value, small }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", fontSize: small ? 7 : 8.5, color: "#4A6D8C", gap: 4, lineHeight: 1.4 }}>
      <span style={{ fontWeight: 800, minWidth: small ? 50 : 64, flexShrink: 0, textTransform: "uppercase", color: "#0D2436", letterSpacing: "0.2px" }}>{label}</span>
      <span style={{ flexShrink: 0, fontWeight: 700, color: "#0D2436" }}>:</span>
      <span style={{ flex: 1, minWidth: 0, color: "#2C5EAD", fontWeight: 600, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

function normalizeRecord(type, r) {
  if (type === "student") {
    return {
      _id: r._id,
      name: r.studentName || "",
      sub: `${r.applyingClass || ""}${r.section ? " - " + r.section : ""}`.trim(),
      phone: r.fatherMobile || r.motherMobile || "",
      photo: r.photo || "",
      idNo: `STU-${r.rollNo ? r.rollNo : shortId(r._id)}`,
      parentName: r.fatherName || r.motherName || r.guardianName || "",
      address: r.address || r.currentAddress || r.permanentAddress || "",
      bloodGroup: r.bloodGroup || "",
      validTill: r.validTill || "",
      session: r.session || "",
      type: "student"
    };
  }
  if (type === "staff") {
    return {
      _id: r._id,
      name: r.name || "",
      sub: `${r.role || "Staff"}${r.dept ? " • " + r.dept : ""}`,
      phone: r.phone || "",
      photo: "",
      idNo: `STF-${shortId(r._id)}`,
      parentName: r.fatherName || r.spouseName || r.guardianName || "",
      address: r.address || "",
      bloodGroup: r.bloodGroup || "",
      validTill: r.validTill || "",
      session: r.session || "",
      type: "staff"
    };
  }
  if (type === "worker") {
    return {
      _id: r._id,
      name: r.name || "",
      sub: r.role || "Worker",
      phone: r.phone || "",
      photo: "",
      idNo: `WRK-${shortId(r._id)}`,
      parentName: "",
      address: r.address || "",
      bloodGroup: r.bloodGroup || "",
      validTill: r.validTill || "",
      session: r.session || "",
      type: "worker"
    };
  }
  if (type === "principal") {
    return {
      _id: r._id,
      name: r.name || "",
      sub: r.title || "Principal",
      phone: r.phone || "",
      photo: "",
      idNo: `PRN-${shortId(r._id)}`,
      parentName: "",
      address: r.address || "",
      bloodGroup: r.bloodGroup || "",
      validTill: r.validTill || "",
      session: r.session || "",
      type: "principal",
      signature: r.signature || r.sign || r.signatureUrl || "",
    };
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════
//  ID CARD FACE
// ═══════════════════════════════════════════════════════════════════════
function IdCardFace({ type, data, small, shape = "rectangle", color, photoShape = "square", principalSignature = "", schoolLogo = "", schoolAddress = SCHOOL_ADDRESS }) {
  const schoolName = useSchoolName();
  const SCHOOL_NAME = `${schoolName} ${SCHOOL_NAME_SUFFIX}`;
  const cfg = TYPE_CONFIG[type];
  const cardColor = color || cfg.color;
  const cardGradient = color ? gradientFromColor(color) : cfg.gradient;
  const shp = SHAPE_CONFIG[shape] || SHAPE_CONFIG.rectangle;
  const w = small ? shp.smallW : shp.w;
  const h = small ? shp.smallH : shp.h;
  const isSquare = shape === "square";
  const photoDims = getPhotoDims(photoShape, small);
  const showParent = cfg.hasParent && data.parentName;

  const barcodeValue = data.idNo ? buildBarcodeScanData({ ...data, type }) : "";
  const barcodeAvailablePx = w - (small ? 20 : 28);

  let barcodeBarWidth = small ? 0.25 : 0.35;
  let barcodeBarHeight = small ? 10 : 14;
  let barcodeFontSize = small ? 4 : 5;

  if (isSquare) {
    barcodeBarWidth = small ? 0.2 : 0.3;
    barcodeBarHeight = small ? 8 : 12;
    barcodeFontSize = small ? 3.5 : 4.5;
  }

  if (!isSquare) {
    return (
      <div
        style={{
          width: w,
          height: h,
          borderRadius: shp.radius,
          overflow: "hidden",
          background: "#FFFFFF",
          boxShadow: "0 2px 6px rgba(13,36,54,0.06), 0 18px 40px -12px rgba(13,71,161,0.28)",
          border: "1px solid rgba(196,226,245,0.9)",
          fontFamily: "'Inter', sans-serif",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <div
          style={{
            background: cardGradient,
            padding: small ? "8px 12px" : "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
              style={{
                width: small ? 30 : 38,
                height: small ? 30 : 38,
                borderRadius: "50%",
                background: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(44,94,173,0.15)",
                overflow: "hidden",
              }}
            >
              {schoolLogo ? (
                <img
                  src={schoolLogo}
                  alt="School Logo"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const sib = e.currentTarget.nextSibling;
                    if (sib) sib.style.display = "flex";
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : null}
              <i
                className="fas fa-graduation-cap"
                style={{ color: cardColor, fontSize: small ? 13 : 17, display: schoolLogo ? "none" : "flex" }}
              ></i>
            </div>
            <div
              style={{
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: small ? 11 : 14,
                lineHeight: 1.2,
                letterSpacing: "0.3px",
                textTransform: "uppercase",
              }}
            >
              {SCHOOL_NAME}
            </div>
          </div>
          {/* Identity Card and ID badges removed */}
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            padding: small ? "8px 12px" : "10px 16px",
            gap: small ? 8 : 12,
            alignItems: "center",
          }}
        >
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: small ? 2 : 3, justifyContent: "center" }}>
            {/* Name and ID added below */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: small ? 7 : 8.5 }}>
              <span style={{ fontWeight: 800, minWidth: small ? 50 : 64, textTransform: "uppercase", color: "#0D2436" }}>Name</span>
              <span style={{ fontWeight: 700, color: "#0D2436" }}>:</span>
              <span style={{ color: "#2C5EAD", fontWeight: 600, whiteSpace: "nowrap" }}>{data.name || "—"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: small ? 7 : 8.5 }}>
              <span style={{ fontWeight: 800, minWidth: small ? 50 : 64, textTransform: "uppercase", color: "#0D2436" }}>ID</span>
              <span style={{ fontWeight: 700, color: "#0D2436" }}>:</span>
              <span style={{ color: "#2C5EAD", fontWeight: 600, whiteSpace: "nowrap" }}>{data.idNo || "—"}</span>
            </div>

            <FieldRow label="Session" value={data.session} small={small} />
            <FieldRow label={type === "student" ? "Class" : "Role"} value={data.sub} small={small} />
            {showParent && <FieldRow label={type === "student" ? "Father Name" : "Guardian"} value={data.parentName} small={small} />}
            <FieldRow label="Address" value={data.address} small={small} />
            <FieldRow label="Phone" value={data.phone} small={small} />
            <FieldRow label="Blood Group" value={data.bloodGroup} small={small} />
            <FieldRow label="Valid Till" value={data.validTill} small={small} />
          </div>

          {data.photo ? (
            <img
              src={data.photo}
              alt={data.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const sib = e.currentTarget.nextSibling;
                if (sib) sib.style.display = "flex";
              }}
              style={{
                width: photoDims.width,
                height: photoDims.height,
                borderRadius: photoDims.borderRadius,
                objectFit: "cover",
                border: `2px solid ${cardColor}`,
                boxShadow: "0 4px 12px rgba(13,71,161,0.18)",
                flexShrink: 0,
              }}
            />
          ) : null}
          <div
            style={{
              width: photoDims.width,
              height: photoDims.height,
              borderRadius: photoDims.borderRadius,
              background: cardGradient,
              display: data.photo ? "none" : "block",
              position: "relative",
              flexShrink: 0,
              boxShadow: "0 4px 12px rgba(13,71,161,0.18)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: small ? 16 : 22,
                lineHeight: 1,
                letterSpacing: "0.5px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {initials(data.name)}
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: small ? "0 10px 4px" : "0 14px 5px",
            background: "#FFFFFF",
          }}
        >
          {data.idNo ? (
            <ScaledBarcode
              value={barcodeValue}
              displayText={data.idNo}
              availablePx={barcodeAvailablePx}
              barWidth={barcodeBarWidth}
              barHeight={barcodeBarHeight}
              fontSize={barcodeFontSize}
            />
          ) : (
            <div style={{ fontSize: small ? 7 : 8.5, color: "#CBD5E1" }}>No ID assigned</div>
          )}
        </div>

        {principalSignature && (
          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", padding: small ? "0 14px 2px" : "0 18px 3px" }}>
            <img
              src={principalSignature}
              alt="Principal Signature"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              style={{ height: small ? 16 : 20, maxWidth: small ? 60 : 80, objectFit: "contain" }}
            />
          </div>
        )}

        <div
          style={{
            background: cardGradient,
            padding: small ? "6px 12px" : "7px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div style={{ color: "rgba(255,255,255,0.9)", fontSize: small ? 6 : 7, lineHeight: 1.3 }}>{schoolAddress}</div>
          <div style={{ color: "#FFFFFF", fontSize: small ? 7.5 : 9, fontWeight: 800, fontStyle: "italic", flexShrink: 0 }}>Principal</div>
        </div>
      </div>
    );
  }

  const parentLabel = type === "student" ? "Father's Name" : "Guardian Name";
  const subLabel = type === "student" ? "Class" : "Role";

  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: shp.radius,
        overflow: "hidden",
        background: "#FFFFFF",
        boxShadow: "0 2px 6px rgba(13,36,54,0.06), 0 18px 40px -12px rgba(13,71,161,0.28)",
        border: "1px solid rgba(196,226,245,0.9)",
        fontFamily: "'Inter', sans-serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div
        style={{
          background: cardGradient,
          clipPath: "polygon(0 0, 100% 0, 100% 78%, 50% 100%, 0 78%)",
          padding: small ? "10px 14px 20px" : "14px 18px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            color: "#FFFFFF",
            fontWeight: 900,
            fontSize: small ? 11 : 15,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
            lineHeight: 1.15,
          }}
        >
          {SCHOOL_NAME}
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.9)",
            fontSize: small ? 5.5 : 7,
            marginTop: 3,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            fontWeight: 600,
          }}
        >
          {SCHOOL_TAGLINE} — Your Children Deserve The Best
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: small ? "2px 14px 0" : "3px 18px 0",
        }}
      >
        <span style={{ fontSize: small ? 6.5 : 8, fontWeight: 800, color: cardColor }}>
          Session- {data.session || "2024-25"}
        </span>
        <div
          style={{
            width: small ? 16 : 20,
            height: small ? 16 : 20,
            borderRadius: "50%",
            background: "#FFFFFF",
            border: `1px solid ${cardColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {schoolLogo ? (
            <img
              src={schoolLogo}
              alt="School Logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const sib = e.currentTarget.nextSibling;
                if (sib) sib.style.display = "flex";
              }}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : null}
          <i
            className="fas fa-graduation-cap"
            style={{ fontSize: small ? 7 : 9, color: cardColor, display: schoolLogo ? "none" : "flex" }}
          ></i>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginTop: small ? 8 : 12 }}>
        {data.photo ? (
          <img
            src={data.photo}
            alt={data.name}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const sib = e.currentTarget.nextSibling;
              if (sib) sib.style.display = "flex";
            }}
            style={{
              width: small ? 54 : 74,
              height: small ? 54 : 74,
              borderRadius: photoDims.borderRadius,
              objectFit: "cover",
              border: `3px solid ${cardColor}`,
              boxShadow: "0 6px 16px rgba(13,71,161,0.22)",
              background: "#FFFFFF",
            }}
          />
        ) : null}
        <div
          style={{
            width: small ? 54 : 74,
            height: small ? 54 : 74,
            borderRadius: photoDims.borderRadius,
            background: cardGradient,
            border: "3px solid #FFFFFF",
            boxShadow: "0 6px 16px rgba(13,71,161,0.22)",
            display: data.photo ? "none" : "block",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: small ? 16 : 21,
              lineHeight: 1,
              letterSpacing: "0.5px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            {initials(data.name)}
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: small ? 4 : 6,
          padding: small ? "10px 16px 6px" : "14px 22px 8px",
          overflow: "hidden",
          justifyContent: "flex-start",
        }}
      >
        <FieldRow label={type === "student" ? "Student Name" : "Name"} value={data.name || "—"} small={small} />
        {showParent && <FieldRow label={parentLabel} value={data.parentName} small={small} />}
        <FieldRow label="Admi. No" value={data.idNo} small={small} />
        <FieldRow label={subLabel} value={data.sub} small={small} />
        <FieldRow label="Address" value={data.address} small={small} />
        <FieldRow label="Contact No" value={data.phone} small={small} />
        <FieldRow label="Blood Group" value={data.bloodGroup} small={small} />
        <FieldRow label="Valid Till" value={data.validTill} small={small} />
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: small ? "1px 8px 2px" : "2px 12px 4px",
          background: "#FFFFFF",
        }}
      >
        {data.idNo ? (
          <ScaledBarcode
            value={barcodeValue}
            displayText={data.idNo}
            availablePx={barcodeAvailablePx}
            barWidth={barcodeBarWidth}
            barHeight={barcodeBarHeight}
            fontSize={barcodeFontSize}
          />
        ) : (
          <div style={{ fontSize: small ? 6 : 7, color: "#CBD5E1" }}>No ID assigned</div>
        )}
      </div>

      {principalSignature && (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            padding: small ? "0 14px 2px" : "0 18px 3px",
          }}
        >
          <img
            src={principalSignature}
            alt="Principal Signature"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            style={{ height: small ? 15 : 20, maxWidth: small ? 56 : 78, objectFit: "contain" }}
          />
          <span
            style={{
              fontSize: small ? 5.5 : 6.5,
              color: "#94A3B8",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              marginTop: 1,
            }}
          >
            Principal Sign
          </span>
        </div>
      )}

      <div
        style={{
          background: cardGradient,
          padding: small ? "5px 16px" : "7px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          textAlign: "center",
          minHeight: small ? 22 : 28,
        }}
      >
        <div
          style={{
            position: "absolute",
            left: small ? 6 : 10,
            width: 0,
            height: 0,
            borderTop: `${small ? 4 : 6}px solid transparent`,
            borderBottom: `${small ? 4 : 6}px solid transparent`,
            borderRight: `${small ? 5 : 7}px solid rgba(255,255,255,0.7)`,
          }}
        />
        <span
          style={{
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: small ? 6 : 7.5,
            lineHeight: 1.25,
            textAlign: "center",
            padding: small ? "0 8px" : "0 10px",
            wordBreak: "break-word",
          }}
        >
          {schoolAddress}
        </span>
        <div
          style={{
            position: "absolute",
            right: small ? 6 : 10,
            width: 0,
            height: 0,
            borderTop: `${small ? 4 : 6}px solid transparent`,
            borderBottom: `${small ? 4 : 6}px solid transparent`,
            borderLeft: `${small ? 5 : 7}px solid rgba(255,255,255,0.7)`,
          }}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function IdCardGenerator() {
  const navigate = useNavigate();

  const [type, setType] = useState("student");
  const [mode, setMode] = useState("single");
  const [shape, setShape] = useState("rectangle");
  const [photoShape, setPhotoShape] = useState("square");
  const [customColor, setCustomColor] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const [principalSignature, setPrincipalSignature] = useState(() => {
    try {
      return localStorage.getItem("idc_principal_signature") || "";
    } catch {
      return "";
    }
  });
  const sigInputRef = useRef(null);

  const [schoolLogo, setSchoolLogo] = useState(() => {
    try {
      return localStorage.getItem("idc_school_logo") || "";
    } catch {
      return "";
    }
  });
  const logoInputRef = useRef(null);

  const [schoolAddress, setSchoolAddress] = useState(() => {
    try {
      return localStorage.getItem("idc_school_address") || SCHOOL_ADDRESS;
    } catch {
      return SCHOOL_ADDRESS;
    }
  });
  const [addressDraft, setAddressDraft] = useState(schoolAddress);

  const [dbRecords, setDbRecords] = useState([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState(null);

  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkGenerated, setBulkGenerated] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const cardRef = useRef(null);
  const fileInputRef = useRef(null);

  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [editorImgObj, setEditorImgObj] = useState(null);
  const [editorZoom, setEditorZoom] = useState(1);
  const [editorOffset, setEditorOffset] = useState({ x: 0, y: 0 });
  const editorCanvasRef = useRef(null);
  const editorDragRef = useRef({ dragging: false, startX: 0, startY: 0, startOffset: { x: 0, y: 0 } });

  const cfg = TYPE_CONFIG[type];
  const effectiveColor = customColor || cfg.color;
  const effectiveGradient = customColor ? gradientFromColor(customColor) : cfg.gradient;
  const formPhotoDims = getPhotoDims(photoShape, false);

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("username");
    navigate("/supervoicerlogin");
  };

  const fetchRecords = useCallback(async (t) => {
    const c = TYPE_CONFIG[t];
    if (!c.hasDb) {
      setDbRecords([]);
      return;
    }
    setLoadingDb(true);
    setDbError(null);
    try {
      const r = await fetch(c.api);
      const data = await r.json();

      let list = [];
      if (t === "principal") {
        if (data && data.data) list = [data.data];
      } else {
        list = Array.isArray(data) ? data : [];
      }

      const normalized = list.map((rec) => normalizeRecord(t, rec)).filter(Boolean);
      setDbRecords(normalized);
    } catch (err) {
      console.error("fetchRecords error:", err);
      setDbError("Could not load records from database. You can still fill details manually.");
      setDbRecords([]);
    } finally {
      setLoadingDb(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords(type);
    setForm({ ...emptyForm, idNo: `${TYPE_CONFIG[type].prefix}-${shortId()}` });
    setBulkSelected(new Set());
    setBulkGenerated(false);
    setSearch("");
  }, [type, fetchRecords]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    try {
      if (principalSignature) localStorage.setItem("idc_principal_signature", principalSignature);
    } catch {
      /* ignore storage errors */
    }
  }, [principalSignature]);

  useEffect(() => {
    try {
      if (schoolLogo) localStorage.setItem("idc_school_logo", schoolLogo);
    } catch {
      /* ignore storage errors */
    }
  }, [schoolLogo]);

  useEffect(() => {
    if (type === "principal" && dbRecords[0] && dbRecords[0].signature && !principalSignature) {
      setPrincipalSignature(dbRecords[0].signature);
    }
  }, [type, dbRecords, principalSignature]);

  const selectRecord = (rec) => {
    setForm({
      sourceId: rec._id,
      name: rec.name,
      idNo: rec.idNo,
      sub: rec.sub,
      phone: rec.phone,
      parentName: rec.parentName || "",
      address: rec.address || "",
      bloodGroup: rec.bloodGroup || "",
      validTill: rec.validTill || "",
      session: rec.session || "",
      photo: rec.photo || "",
    });
    setToast({ type: "success", text: `${rec.name} details loaded. You can edit/add photo below.` });
  };

  const setField = (field) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setToast({ type: "error", text: "Please select an image file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => openPhotoEditor(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const getEditorDims = useCallback(() => {
    const pcfg = PHOTO_SHAPE_CONFIG[photoShape] || PHOTO_SHAPE_CONFIG.square;
    return pcfg.tall ? { w: 260, h: 330 } : { w: 320, h: 320 };
  }, [photoShape]);

  const openPhotoEditor = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      setEditorImgObj(img);
      setEditorZoom(1);
      setEditorOffset({ x: 0, y: 0 });
      setPhotoEditorOpen(true);
    };
    img.onerror = () => setToast({ type: "error", text: "Could not load that image." });
    img.src = dataUrl;
  };

  const redrawEditorCanvas = useCallback(() => {
    const canvas = editorCanvasRef.current;
    if (!canvas || !editorImgObj) return;
    const { w, h } = getEditorDims();
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#C4E2F5";
    ctx.fillRect(0, 0, w, h);
    const baseScale = Math.max(w / editorImgObj.naturalWidth, h / editorImgObj.naturalHeight);
    const scale = baseScale * editorZoom;
    ctx.save();
    ctx.translate(w / 2 + editorOffset.x, h / 2 + editorOffset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(editorImgObj, -editorImgObj.naturalWidth / 2, -editorImgObj.naturalHeight / 2);
    ctx.restore();
  }, [editorImgObj, editorZoom, editorOffset, getEditorDims]);

  useEffect(() => {
    if (photoEditorOpen) redrawEditorCanvas();
  }, [photoEditorOpen, redrawEditorCanvas]);

  const handleEditorPointerDown = (e) => {
    const point = e.touches ? e.touches[0] : e;
    editorDragRef.current = { dragging: true, startX: point.clientX, startY: point.clientY, startOffset: { ...editorOffset } };
  };
  const handleEditorPointerMove = (e) => {
    if (!editorDragRef.current.dragging) return;
    if (e.touches) e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - editorDragRef.current.startX;
    const dy = point.clientY - editorDragRef.current.startY;
    setEditorOffset({ x: editorDragRef.current.startOffset.x + dx, y: editorDragRef.current.startOffset.y + dy });
  };
  const handleEditorPointerUp = () => {
    editorDragRef.current.dragging = false;
  };
  const handleEditorWheel = (e) => {
    e.preventDefault();
    setEditorZoom((z) => Number(Math.min(4, Math.max(1, z - e.deltaY * 0.0015)).toFixed(3)));
  };

  const resetPhotoEdit = () => {
    setEditorZoom(1);
    setEditorOffset({ x: 0, y: 0 });
  };

  const cancelPhotoEdit = () => {
    setPhotoEditorOpen(false);
  };

  const applyPhotoEdit = () => {
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    setForm((prev) => ({ ...prev, photo: dataUrl }));
    setPhotoEditorOpen(false);
    setToast({ type: "success", text: "Photo adjusted!" });
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setToast({ type: "error", text: "Please select an image file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPrincipalSignature(reader.result);
      setToast({ type: "success", text: "Signature saved — it will now appear automatically on every ID card." });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setPrincipalSignature("");
    try {
      localStorage.removeItem("idc_principal_signature");
    } catch {
      /* ignore storage errors */
    }
    setToast({ type: "warn", text: "Signature removed." });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setToast({ type: "error", text: "Please select an image file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSchoolLogo(reader.result);
      setToast({ type: "success", text: "School logo saved — it will now appear automatically on every ID card." });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveLogo = () => {
    setSchoolLogo("");
    try {
      localStorage.removeItem("idc_school_logo");
    } catch {
      /* ignore storage errors */
    }
    setToast({ type: "warn", text: "School logo removed." });
  };

  const handleSaveAddress = () => {
    const trimmed = (addressDraft || "").trim();
    const finalAddress = trimmed || SCHOOL_ADDRESS;
    setSchoolAddress(finalAddress);
    setAddressDraft(finalAddress);
    try {
      localStorage.setItem("idc_school_address", finalAddress);
    } catch {
      /* ignore storage errors */
    }
    setToast({ type: "success", text: "School address saved — it will now appear automatically on every ID card." });
  };

  const handleResetAddress = () => {
    setSchoolAddress(SCHOOL_ADDRESS);
    setAddressDraft(SCHOOL_ADDRESS);
    try {
      localStorage.removeItem("idc_school_address");
    } catch {
      /* ignore storage errors */
    }
    setToast({ type: "warn", text: "School address reset to default." });
  };

  const filteredDb = useMemo(() => {
    if (!search.trim()) return dbRecords;
    const q = search.trim().toLowerCase();
    return dbRecords.filter((r) => r.name.toLowerCase().includes(q) || r.idNo.toLowerCase().includes(q));
  }, [dbRecords, search]);

  // ═══════════════════════════════════════════════════════════════════
  // FIXED PRINT FUNCTION - uses print area with exact live preview
  // ═══════════════════════════════════════════════════════════════════
  const handlePrintSingle = () => {
    const printArea = document.getElementById('idc-print-area');
    if (!printArea) return;

    // Show print area, hide everything else
    const originalTitle = document.title;
    document.title = 'ID Card';

    window.print();

    // Restore title after print
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  // ═══════════════════════════════════════════════════════════════════
  // FIXED DOWNLOAD - ensures PDF matches live preview exactly
  // ═══════════════════════════════════════════════════════════════════
  const handleDownloadSingle = async () => {
    if (!form.name.trim()) {
      setToast({ type: "warn", text: "Enter a name before downloading." });
      return;
    }
    setDownloading(true);
    try {
      // Get the card element directly
      const cardElement = cardRef.current;
      if (!cardElement) {
        setToast({ type: "error", text: "Card not found." });
        setDownloading(false);
        return;
      }

      // Wait for images and fonts
      await waitForImages(cardElement);
      await waitForFontsReady();

      // Capture the card with high quality
      const canvas = await html2canvas(cardElement, {
        scale: 3,
        backgroundColor: "#FFFFFF",
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
        width: cardElement.scrollWidth,
        height: cardElement.scrollHeight,
        onclone: (clonedDoc, element) => {
          // Ensure all images are loaded in the clone
          const imgs = element.querySelectorAll('img');
          imgs.forEach(img => {
            if (img.complete && img.naturalWidth === 0) {
              // Image failed to load, try to fix
              img.style.display = 'none';
            }
          });
        }
      });

      // Get the exact dimensions of the captured card
      const imgData = canvas.toDataURL("image/png");
      const pxW = canvas.width;
      const pxH = canvas.height;
      const aspect = pxH / pxW;

      // Create PDF with exact aspect ratio
      const pageWmm = 85;
      const pageHmm = Number((pageWmm * aspect).toFixed(2));

      const pdf = new jsPDF({
        orientation: pageHmm > pageWmm ? "portrait" : "landscape",
        unit: "mm",
        format: [pageWmm, pageHmm],
      });

      // Add image filling the entire page
      pdf.addImage(imgData, "PNG", 0, 0, pageWmm, pageHmm);
      pdf.save(`ID_${form.name.replace(/\s+/g, "_")}.pdf`);
      setToast({ type: "success", text: "ID card downloaded!" });
    } catch (err) {
      console.error("download error:", err);
      setToast({ type: "error", text: "Failed to generate PDF. Please try again." });
    } finally {
      setDownloading(false);
    }
  };

  const toggleBulk = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (bulkSelected.size === filteredDb.length) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(filteredDb.map((r) => r._id)));
    }
  };

  const selectedRecords = useMemo(
    () => dbRecords.filter((r) => bulkSelected.has(r._id)),
    [dbRecords, bulkSelected]
  );

  const bulkPages = useMemo(() => {
    const pages = [];
    for (let i = 0; i < selectedRecords.length; i += 8) {
      pages.push(selectedRecords.slice(i, i + 8));
    }
    return pages;
  }, [selectedRecords]);

  const handleGenerateBulk = () => {
    if (selectedRecords.length === 0) {
      setToast({ type: "warn", text: "Select at least one person first." });
      return;
    }
    setBulkGenerated(true);
  };

  const handlePrintBulk = () => {
    window.print();
  };

  const handleDownloadBulk = async () => {
    setDownloading(true);
    try {
      const pageEls = document.querySelectorAll(".id-bulk-page");
      if (pageEls.length === 0) {
        setToast({ type: "warn", text: "Nothing to download." });
        setDownloading(false);
        return;
      }
      const pdf = new jsPDF("p", "mm", "a4");
      const A4_W = 210;
      const A4_H = 297;
      const marginMm = 5;
      const maxW = A4_W - marginMm * 2;
      const maxH = A4_H - marginMm * 2;

      await waitForFontsReady();
      for (let i = 0; i < pageEls.length; i++) {
        await waitForImages(pageEls[i]);
        const canvas = await html2canvas(pageEls[i], {
          scale: 2,
          backgroundColor: "#FFFFFF",
          useCORS: true,
          allowTaint: true,
          imageTimeout: 5000,
        });
        const imgData = canvas.toDataURL("image/png");

        const aspect = canvas.height / canvas.width;
        let drawW = maxW;
        let drawH = drawW * aspect;
        if (drawH > maxH) {
          drawH = maxH;
          drawW = drawH / aspect;
        }
        const offsetX = marginMm + (maxW - drawW) / 2;
        const offsetY = marginMm + (maxH - drawH) / 2;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", offsetX, offsetY, drawW, drawH);
      }
      pdf.save(`ID_Cards_${cfg.label}_Bulk.pdf`);
      setToast({ type: "success", text: `${selectedRecords.length} ID cards downloaded!` });
    } catch (err) {
      console.error("bulk download error:", err);
      setToast({ type: "error", text: "Failed to generate bulk PDF." });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #EAF4FD; font-family: 'Inter', sans-serif; }
        .idc-btn { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
        .idc-btn:hover { transform: translateY(-2px); filter: brightness(1.03); box-shadow: 0 10px 22px -8px rgba(13,71,161,0.35); }
        .idc-btn:active { transform: translateY(0); }
        .idc-tab { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
        .idc-tab:hover { transform: translateY(-1px); }
        .idc-row { transition: all 0.15s ease; }
        .idc-row:hover { background: #EAF4FD !important; border-color: #4BB8FA !important; transform: translateX(2px); }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        /* ═══════════════════════════════════════════════════════════════ */
        /* FIXED PRINT STYLES - exact live preview matching */
        /* ═══════════════════════════════════════════════════════════════ */
        @media print {
          /* Hide everything except the print area */
          body * {
            visibility: hidden !important;
          }
          
          #idc-print-area, 
          #idc-print-area * {
            visibility: visible !important;
          }
          
          #idc-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #FFFFFF !important;
            padding: 20px !important;
          }

          /* Ensure the card renders properly in print */
          #idc-print-area > div {
            transform: scale(1) !important;
            max-width: 100% !important;
            max-height: 100% !important;
          }

          /* Card should be centered and properly sized */
          .id-bulk-page {
            page-break-after: always !important;
            page-break-inside: avoid !important;
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 16px !important;
            padding: 20px !important;
            background: #FFFFFF !important;
          }

          /* Ensure images print properly */
          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Ensure gradients and backgrounds print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        /* ── MOBILE RESPONSIVE STYLES ── */
        @media (max-width: 768px) {
          /* Header - stack on mobile */
          .idc-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            padding: 12px 16px !important;
          }
          .idc-header-left {
            justify-content: center !important;
          }
          .idc-header-right {
            justify-content: center !important;
          }
          
          /* Type tabs - wrap on mobile */
          .idc-type-tabs {
            justify-content: center !important;
            gap: 4px !important;
          }
          .idc-type-tabs button {
            font-size: 9px !important;
            padding: 5px 10px !important;
          }

          /* Controls row - stack on mobile */
          .idc-controls-row {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .idc-controls-row > div {
            border-left: none !important;
            padding-left: 0 !important;
            padding-top: 8px !important;
            border-top: 1px solid #C4E2F5 !important;
            width: 100% !important;
          }
          .idc-controls-row > div:first-child {
            border-top: none !important;
            padding-top: 0 !important;
          }

          /* Single mode - full width on mobile */
          .idc-single-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
            width: 100% !important;
          }
          
          /* Form fields - full width on mobile */
          .idc-form-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
            width: 100% !important;
          }
          .idc-form-fields {
            grid-template-columns: 1fr !important;
          }
          .idc-form-fields > div {
            width: 100% !important;
          }
          
          /* Preview - full width on mobile */
          .idc-preview-container {
            width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            padding: 0 !important;
          }
          .idc-preview-container > div {
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }
          
          /* Card wrapper - full width */
          .idc-preview-container > div > div {
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }

          /* Bulk mode - single column on mobile */
          .idc-bulk-grid {
            grid-template-columns: 1fr !important;
          }
          .id-bulk-page {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
            padding: 12px !important;
          }

          /* Background balls - smaller on mobile */
          .idc-bg-ball-1 { width: 60px; height: 60px; }
          .idc-bg-ball-2 { width: 50px; height: 50px; }
          .idc-bg-ball-3 { width: 40px; height: 40px; }
          .idc-bg-ball-4 { width: 35px; height: 35px; }
          
          /* Photo upload section - wrap on mobile */
          .idc-photo-section {
            flex-direction: column !important;
            align-items: center !important;
          }
          .idc-photo-buttons {
            justify-content: center !important;
          }
          
          /* Print/download buttons - full width on mobile */
          .idc-action-buttons {
            flex-direction: column !important;
          }
          .idc-action-buttons button {
            width: 100% !important;
          }
          
          /* Remove extra padding from main container */
          .idc-single-grid > div:last-child {
            padding: 0 !important;
          }
          
          /* Form container - full width no gaps */
          .idc-form-grid > div:first-child {
            width: 100% !important;
          }
          
          /* Card container - full width */
          #idc-print-area {
            width: 100% !important;
          }
          #idc-print-area > div {
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }

          /* Settings grid - single column on mobile */
          .idc-settings-grid {
            grid-template-columns: 1fr !important;
          }

          /* Settings fields - stack */
          .idc-settings-grid > div {
            width: 100% !important;
          }
        }

        @media (max-width: 480px) {
          .idc-type-tabs button {
            font-size: 8px !important;
            padding: 4px 8px !important;
          }
          .idc-header-left div:last-child {
            font-size: 11px !important;
          }
          .idc-header-left div:last-child div {
            font-size: 7px !important;
          }
          .idc-header-left {
            gap: 6px !important;
          }
          .idc-header-left div:first-child {
            width: 32px !important;
            height: 32px !important;
            font-size: 14px !important;
          }
        }

        /* ── Animated Background SMALL BALLS (CLEAR, NO BLUR) ── */
        .idc-bg-ball {
          position: fixed;
          border-radius: 50%;
          z-index: -1;
          pointer-events: none;
        }
        
        /* Ball 1 - Small Blue, Top Right */
        .idc-bg-ball-1 {
          top: 12%;
          right: 8%;
          width: 110px;
          height: 110px;
          background: radial-gradient(circle at 30% 30%, #E3F2FD, #2C5EAD 80%);
          opacity: 0.25;
          animation: idcFloat1 16s ease-in-out infinite alternate;
        }
        
        /* Ball 2 - Small Silver/Cream, Bottom Left */
        .idc-bg-ball-2 {
          bottom: 15%;
          left: 6%;
          width: 90px;
          height: 90px;
          background: radial-gradient(circle at 70% 70%, #FFFFFF, #C4E2F5 80%);
          opacity: 0.3;
          animation: idcFloat2 14s ease-in-out infinite alternate;
        }

        /* Ball 3 - Medium Blue, Middle Right */
        .idc-bg-ball-3 {
          top: 50%;
          right: 15%;
          width: 70px;
          height: 70px;
          background: radial-gradient(circle, #C4E2F5 20%, #1591DC 90%);
          opacity: 0.2;
          animation: idcFloat1 18s ease-in-out infinite alternate-reverse;
        }

        /* Ball 4 - Small Light Blue, Middle Left */
        .idc-bg-ball-4 {
          top: 35%;
          left: 12%;
          width: 60px;
          height: 60px;
          background: radial-gradient(circle, #FFFFFF, #C4E2F5 60%, #4BB8FA 90%);
          opacity: 0.25;
          animation: idcFloat2 12s ease-in-out infinite alternate-reverse;
        }

        @keyframes idcFloat1 {
          0%   { transform: translate(0px, 0px) scale(1); }
          50%  { transform: translate(-20px, 25px) scale(1.08); }
          100% { transform: translate(15px, -20px) scale(0.95); }
        }
        @keyframes idcFloat2 {
          0%   { transform: translate(0px, 0px) scale(1); }
          50%  { transform: translate(20px, -20px) scale(1.05); }
          100% { transform: translate(-15px, 15px) scale(0.92); }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#EAF4FD", fontFamily: "'Inter', sans-serif", position: "relative", overflow: "hidden" }}>

        {/* ── BACKGROUND BALLS ── */}
        <div className="idc-bg-ball idc-bg-ball-1"></div>
        <div className="idc-bg-ball idc-bg-ball-2"></div>
        <div className="idc-bg-ball idc-bg-ball-3"></div>
        <div className="idc-bg-ball idc-bg-ball-4"></div>

        {/* HEADER - PREMIUM BLUE THEME */}
        <div
          className="idc-header"
          style={{
            background: "linear-gradient(120deg, #103B78 0%, #2C5EAD 55%, #1591DC 100%)",
            padding: "16px 26px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 10px 30px -10px rgba(13,36,54,0.45)",
            borderBottom: "1px solid rgba(75,184,250,0.55)",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div className="idc-header-left" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14, background: "linear-gradient(135deg, #4BB8FA, #C4E2F5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: "0 6px 16px rgba(13,36,54,0.35), inset 0 0 0 1px rgba(255,255,255,0.4)" }}>
              <i className="fas fa-id-card" style={{ color: "#103B78" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#FFFFFF", letterSpacing: "0.2px" }}>ID Card Generator</div>
              <div style={{ fontSize: 9, color: "#CFE9FB", letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 700 }}>
                Students · Staff · Workers · Principal · Office Admin
              </div>
            </div>
          </div>
          <div className="idc-header-right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={handleLogout}
              className="idc-btn"
              style={{ background: "linear-gradient(135deg, #E53935, #C62828)", color: "#FFFFFF", border: "none", padding: "8px 16px", borderRadius: 10, fontSize: 11, fontWeight: 700, boxShadow: "0 4px 12px rgba(198,40,40,0.35)" }}
            >
              <i className="fas fa-sign-out-alt" style={{ marginRight: 6 }}></i>Logout
            </button>
            <button
              onClick={() => navigate(-1)}
              className="idc-btn"
              style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "8px 16px", borderRadius: 10, fontSize: 11, fontWeight: 700, boxShadow: "0 2px 8px rgba(13,36,54,0.12)" }}
            >
              <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i>Back
            </button>
          </div>
        </div>

        {toast && (
          <div
            style={{
              position: "fixed", top: 76, right: 24, zIndex: 2000, animation: "fadeUp 0.3s ease",
              background: toast.type === "success" ? "#E3F2FD" : toast.type === "warn" ? "#FFF3E0" : "#FFEBEE",
              color: toast.type === "success" ? "#2C5EAD" : toast.type === "warn" ? "#E65100" : "#C62828",
              border: `1px solid ${toast.type === "success" ? "#C4E2F5" : toast.type === "warn" ? "#FFE0B2" : "#FFCDD2"}`,
              padding: "12px 18px", borderRadius: 14, fontSize: 13, fontWeight: 600, boxShadow: "0 12px 30px -8px rgba(13,36,54,0.25)", maxWidth: 320,
            }}
          >
            {toast.text}
          </div>
        )}

        {/* PHOTO ADJUST MODAL */}
        {photoEditorOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(13,36,54,0.55)",
              backdropFilter: "blur(2px)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div style={{ background: "#FFFFFF", borderRadius: 22, padding: 24, width: 400, maxWidth: "94vw", boxShadow: "0 30px 70px -12px rgba(13,36,54,0.45)" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#2C5EAD", marginBottom: 4 }}>
                <i className="fas fa-crop-alt" style={{ marginRight: 8, color: effectiveColor }}></i>Adjust Photo
              </div>
              <div style={{ fontSize: 11, color: "#4A6D8C", marginBottom: 14 }}>
                Drag the photo to move it · scroll or use the slider to zoom in / zoom out
              </div>

              <div
                style={{
                  width: getEditorDims().w,
                  height: getEditorDims().h,
                  margin: "0 auto",
                  borderRadius: photoShape === "circle" ? "50%" : photoShape === "square" ? 12 : 8,
                  overflow: "hidden",
                  border: `2px solid ${effectiveColor}`,
                  cursor: "grab",
                  touchAction: "none",
                  background: "#C4E2F5",
                }}
                onMouseDown={handleEditorPointerDown}
                onMouseMove={handleEditorPointerMove}
                onMouseUp={handleEditorPointerUp}
                onMouseLeave={handleEditorPointerUp}
                onTouchStart={handleEditorPointerDown}
                onTouchMove={handleEditorPointerMove}
                onTouchEnd={handleEditorPointerUp}
                onWheel={handleEditorWheel}
              >
                <canvas ref={editorCanvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                <i className="fas fa-search-minus" style={{ color: "#4A6D8C", fontSize: 12 }}></i>
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.01"
                  value={editorZoom}
                  onChange={(e) => setEditorZoom(Number(e.target.value))}
                  style={{ flex: 1 }}
                />
                <i className="fas fa-search-plus" style={{ color: "#4A6D8C", fontSize: 12 }}></i>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button
                  className="idc-btn"
                  onClick={resetPhotoEdit}
                  style={{ flex: 1, background: "#C4E2F5", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: 11, borderRadius: 12, fontSize: 12, fontWeight: 700 }}
                >
                  <i className="fas fa-rotate-left" style={{ marginRight: 6 }}></i>Reset
                </button>
                <button
                  className="idc-btn"
                  onClick={cancelPhotoEdit}
                  style={{ flex: 1, background: "#C4E2F5", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: 11, borderRadius: 12, fontSize: 12, fontWeight: 700 }}
                >
                  Cancel
                </button>
                <button
                  className="idc-btn"
                  onClick={applyPhotoEdit}
                  style={{ flex: 1, background: effectiveGradient, color: "#FFFFFF", border: "none", padding: 11, borderRadius: 12, fontSize: 12, fontWeight: 800, boxShadow: "0 6px 16px -4px rgba(13,71,161,0.5)" }}
                >
                  <i className="fas fa-check" style={{ marginRight: 6 }}></i>Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: "20px 24px 40px", position: "relative", zIndex: 1 }}>
          {/* TYPE TABS */}
          <div className="idc-type-tabs" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {Object.entries(TYPE_CONFIG).map(([key, c]) => (
              <button
                key={key}
                className="idc-tab"
                onClick={() => setType(key)}
                style={{
                  padding: "9px 18px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 700,
                  border: type === key ? "none" : "1px solid #C4E2F5",
                  background: type === key ? c.gradient : "#FFFFFF",
                  color: type === key ? "#FFFFFF" : "#2C5EAD",
                  boxShadow: type === key ? "0 8px 18px -6px rgba(13,71,161,0.45)" : "0 2px 6px rgba(13,36,54,0.06)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className={`fas ${c.icon}`}></i>{c.label}
              </button>
            ))}
          </div>

          {/* MODE TOGGLE + SETTINGS TRIGGER */}
          <div className="idc-controls-row" style={{ display: "flex", gap: 12, marginBottom: showSettings ? 14 : 20, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                className="idc-btn"
                onClick={() => setMode("single")}
                style={{ padding: "9px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700, border: mode === "single" ? "none" : "1px solid #C4E2F5", background: mode === "single" ? "linear-gradient(135deg, #103B78, #2C5EAD)" : "#FFFFFF", color: mode === "single" ? "#FFFFFF" : "#2C5EAD", boxShadow: mode === "single" ? "0 8px 18px -6px rgba(13,71,161,0.45)" : "none" }}
              >
                <i className="fas fa-id-badge" style={{ marginRight: 6 }}></i>Single Card
              </button>
              <button
                className="idc-btn"
                onClick={() => setMode("bulk")}
                disabled={!cfg.hasDb}
                style={{ padding: "9px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700, border: mode === "bulk" ? "none" : "1px solid #C4E2F5", background: mode === "bulk" ? "linear-gradient(135deg, #103B78, #2C5EAD)" : "#FFFFFF", color: !cfg.hasDb ? "#CBD5E1" : mode === "bulk" ? "#FFFFFF" : "#2C5EAD", cursor: !cfg.hasDb ? "not-allowed" : "pointer", boxShadow: mode === "bulk" ? "0 8px 18px -6px rgba(13,71,161,0.45)" : "none" }}
              >
                <i className="fas fa-layer-group" style={{ marginRight: 6 }}></i>Bulk Generate
              </button>
              {!cfg.hasDb && (
                <span style={{ fontSize: 11, color: "#4A6D8C", alignSelf: "center" }}>
                  Office Admin has no database — single card (manual) only.
                </span>
              )}
            </div>

            {/* Single "Settings" toggle — everything else (shape, photo shape, color, logo, signature, barcode note) lives inside */}
            <button
              className="idc-btn"
              onClick={() => setShowSettings(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700,
                border: showSettings ? "none" : "1px solid #C4E2F5",
                background: showSettings ? cfg.gradient : "#FFFFFF",
                color: showSettings ? "#FFFFFF" : "#2C5EAD",
                boxShadow: showSettings ? "0 8px 18px -6px rgba(13,71,161,0.45)" : "0 2px 6px rgba(13,36,54,0.06)",
              }}
            >
              <i className="fas fa-sliders-h"></i> Settings
              <i className={`fas fa-chevron-${showSettings ? "up" : "down"}`} style={{ fontSize: 10 }}></i>
            </button>
          </div>

          {showSettings && (
            <div
              className="idc-settings-grid"
              style={{
                background: "#F4F9FE",
                border: "1px solid #C4E2F5",
                borderRadius: 18,
                padding: 18,
                marginBottom: 20,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 8px 24px -12px rgba(13,71,161,0.15)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: 14,
              }}
            >
              {/* Card shape */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-vector-square" style={{ marginRight: 6 }}></i>Card Shape
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {Object.entries(SHAPE_CONFIG).map(([key, s]) => (
                    <button
                      key={key}
                      className="idc-btn"
                      onClick={() => setShape(key)}
                      style={{
                        padding: "7px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700,
                        border: shape === key ? "none" : "1px solid #C4E2F5",
                        background: shape === key ? cfg.gradient : "#FFFFFF",
                        color: shape === key ? "#FFFFFF" : "#2C5EAD",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <i className={`fas ${s.icon}`}></i>{s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo shape */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-image" style={{ marginRight: 6 }}></i>Photo Shape
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {Object.entries(PHOTO_SHAPE_CONFIG).map(([key, s]) => (
                    <button
                      key={key}
                      className="idc-btn"
                      onClick={() => setPhotoShape(key)}
                      style={{
                        padding: "7px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700,
                        border: photoShape === key ? "none" : "1px solid #C4E2F5",
                        background: photoShape === key ? cfg.gradient : "#FFFFFF",
                        color: photoShape === key ? "#FFFFFF" : "#2C5EAD",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <i className={`fas ${s.icon}`}></i>{s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Card color */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-palette" style={{ marginRight: 6 }}></i>Card Color
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      className="idc-btn"
                      onClick={() => setCustomColor(c)}
                      title={c}
                      style={{
                        width: 22, height: 22, borderRadius: "50%", background: c,
                        border: effectiveColor === c ? "2px solid #2C5EAD" : "1px solid #C4E2F5",
                        boxShadow: effectiveColor === c ? "0 0 0 2px #FFFFFF inset" : "none",
                        padding: 0,
                      }}
                    />
                  ))}
                  <label
                    title="Pick any custom color"
                    style={{
                      width: 22, height: 22, borderRadius: "50%", border: "1px dashed #4A6D8C",
                      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                      position: "relative", overflow: "hidden",
                      background: customColor && !COLOR_PRESETS.includes(customColor) ? customColor : "#FFFFFF",
                    }}
                  >
                    <i className="fas fa-eye-dropper" style={{ fontSize: 9, color: customColor && !COLOR_PRESETS.includes(customColor) ? "#FFFFFF" : "#4A6D8C" }}></i>
                    <input
                      type="color"
                      value={customColor || cfg.color}
                      onChange={(e) => setCustomColor(e.target.value)}
                      style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                    />
                  </label>
                  {customColor && (
                    <button
                      className="idc-btn"
                      onClick={() => setCustomColor(null)}
                      style={{ background: "#C4E2F5", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "5px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              {/* School logo */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-school" style={{ marginRight: 6 }}></i>School Logo
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {schoolLogo ? (
                    <img
                      src={schoolLogo}
                      alt="School logo"
                      style={{ height: 28, width: 28, objectFit: "cover", borderRadius: "50%", border: "1px solid #C4E2F5", background: "#FFFFFF" }}
                    />
                  ) : (
                    <span style={{ fontSize: 10.5, color: "#4A6D8C" }}>Not uploaded</span>
                  )}
                  <button
                    className="idc-btn"
                    onClick={() => logoInputRef.current && logoInputRef.current.click()}
                    style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "6px 12px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}
                  >
                    <i className="fas fa-image" style={{ marginRight: 6 }}></i>{schoolLogo ? "Change" : "Upload"}
                  </button>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                  {schoolLogo && (
                    <button
                      className="idc-btn"
                      onClick={handleRemoveLogo}
                      style={{ background: "#FFEBEE", color: "#C62828", border: "none", padding: "6px 10px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* School address */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-map-marker-alt" style={{ marginRight: 6 }}></i>School Address
                </div>
                <input
                  value={addressDraft}
                  onChange={(e) => setAddressDraft(e.target.value)}
                  placeholder="Enter school address"
                  style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12, marginBottom: 8 }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="idc-btn"
                    onClick={handleSaveAddress}
                    disabled={addressDraft === schoolAddress}
                    style={{
                      background: addressDraft === schoolAddress ? "#F4F9FE" : cfg.gradient,
                      color: addressDraft === schoolAddress ? "#4A6D8C" : "#FFFFFF",
                      border: addressDraft === schoolAddress ? "1px solid #C4E2F5" : "none",
                      padding: "6px 12px", borderRadius: 8, fontSize: 10.5, fontWeight: 700,
                      cursor: addressDraft === schoolAddress ? "not-allowed" : "pointer",
                    }}
                  >
                    <i className="fas fa-save" style={{ marginRight: 6 }}></i>Save
                  </button>
                  {schoolAddress !== SCHOOL_ADDRESS && (
                    <button
                      className="idc-btn"
                      onClick={handleResetAddress}
                      style={{ background: "#FFEBEE", color: "#C62828", border: "none", padding: "6px 10px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}
                    >
                      Reset to Default
                    </button>
                  )}
                </div>
              </div>

              {/* Principal signature */}
              <div style={{ background: "#FFFFFF", borderRadius: 14, padding: 14, border: "1px solid #E1EEF9", boxShadow: "0 2px 8px rgba(13,36,54,0.05)" }}>
                <div style={{ fontSize: 10.5, color: "#4A6D8C", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 10 }}>
                  <i className="fas fa-signature" style={{ marginRight: 6 }}></i>Principal Sign
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {principalSignature ? (
                    <img
                      src={principalSignature}
                      alt="Principal signature"
                      style={{ height: 28, maxWidth: 90, objectFit: "contain", border: "1px solid #C4E2F5", borderRadius: 6, background: "#FFFFFF", padding: "2px 6px" }}
                    />
                  ) : (
                    <span style={{ fontSize: 10.5, color: "#4A6D8C" }}>Not uploaded</span>
                  )}
                  <button
                    className="idc-btn"
                    onClick={() => sigInputRef.current && sigInputRef.current.click()}
                    style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "6px 12px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}
                  >
                    <i className="fas fa-signature" style={{ marginRight: 6 }}></i>{principalSignature ? "Change" : "Upload"}
                  </button>
                  <input ref={sigInputRef} type="file" accept="image/*" onChange={handleSignatureUpload} style={{ display: "none" }} />
                  {principalSignature && (
                    <button
                      className="idc-btn"
                      onClick={handleRemoveSignature}
                      style={{ background: "#FFEBEE", color: "#C62828", border: "none", padding: "6px 10px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Barcode info — full width */}
              <div style={{ gridColumn: "1 / -1", background: "#FFFFFF", borderRadius: 12, padding: "12px 14px", border: "1px solid #E1EEF9", fontSize: 11, color: "#4A6D8C" }}>
                <i className="fas fa-info-circle" style={{ marginRight: 6, color: "#2C5EAD" }}></i>
                <b style={{ color: "#2C5EAD" }}>Barcode:</b> Visually SMALL but encodes ALL data with FULL length.
                When scanned, displays: Name, Father, Phone, Address, Blood Group, ID, Type.
              </div>
            </div>
          )}

          {/* ══════════════ SINGLE MODE ══════════════ */}
          {mode === "single" && (
            <div className="idc-single-grid" style={{ display: "grid", gridTemplateColumns: cfg.hasDb ? "320px 1fr" : "1fr", gap: 20 }}>
              {/* LEFT: search DB */}
              {cfg.hasDb && (
                <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid #C4E2F5", padding: 16, alignSelf: "start", boxShadow: "0 4px 16px -8px rgba(13,36,54,0.1)" }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#2C5EAD", marginBottom: 10 }}>
                    <i className="fas fa-database" style={{ marginRight: 6, color: cfg.color }}></i>Load from Database
                  </div>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or ID..."
                    style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "8px 12px", fontSize: 12, marginBottom: 10 }}
                  />
                  {dbError && <div style={{ fontSize: 11, color: "#C62828", marginBottom: 8 }}>{dbError}</div>}
                  <div style={{ maxHeight: 380, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                    {loadingDb ? (
                      <div style={{ textAlign: "center", padding: 20, color: "#4A6D8C", fontSize: 12 }}>
                        <i className="fas fa-spinner fa-pulse"></i> Loading...
                      </div>
                    ) : filteredDb.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 20, color: "#4A6D8C", fontSize: 12 }}>No records found</div>
                    ) : (
                      filteredDb.map((rec) => (
                        <div
                          key={rec._id}
                          className="idc-row"
                          onClick={() => selectRecord(rec)}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, border: "1px solid #C4E2F5", cursor: "pointer" }}
                        >
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: cfg.gradient, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                            {initials(rec.name)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#2C5EAD", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{rec.name}</div>
                            <div style={{ fontSize: 10, color: "#4A6D8C" }}>{rec.sub}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* RIGHT: manual form + preview */}
              <div className="idc-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "start" }}>
                <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid #C4E2F5", padding: 20, boxShadow: "0 4px 16px -8px rgba(13,36,54,0.1)" }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#2C5EAD", marginBottom: 16 }}>
                    <i className="fas fa-pen" style={{ marginRight: 6, color: cfg.color }}></i>Card Details {cfg.hasDb && <span style={{ fontWeight: 400, fontSize: 11, color: "#4A6D8C" }}>(edit freely, or fill manually)</span>}
                  </div>

                  {/* Photo upload */}
                  <div className="idc-photo-section" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                    {form.photo ? (
                      <img
                        src={form.photo}
                        alt="preview"
                        style={{ width: formPhotoDims.width, height: formPhotoDims.height, borderRadius: formPhotoDims.borderRadius, objectFit: "cover", border: `2px solid ${effectiveColor}`, boxShadow: "0 4px 12px rgba(13,71,161,0.18)" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: formPhotoDims.width,
                          height: formPhotoDims.height,
                          borderRadius: formPhotoDims.borderRadius,
                          background: effectiveGradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#FFFFFF",
                          fontWeight: 800,
                          boxShadow: "0 4px 12px rgba(13,71,161,0.18)",
                        }}
                      >
                        {initials(form.name)}
                      </div>
                    )}
                    <div className="idc-photo-buttons" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <button
                        className="idc-btn"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "7px 14px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}
                      >
                        <i className="fas fa-camera" style={{ marginRight: 6 }}></i>Upload Photo
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
                      {form.photo && (
                        <button
                          className="idc-btn"
                          onClick={() => openPhotoEditor(form.photo)}
                          style={{ background: "#E3F2FD", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "7px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}
                        >
                          <i className="fas fa-crop-alt" style={{ marginRight: 6 }}></i>Adjust
                        </button>
                      )}
                      {form.photo && (
                        <button
                          className="idc-btn"
                          onClick={() => setForm((p) => ({ ...p, photo: "" }))}
                          style={{ background: "#FFEBEE", color: "#C62828", border: "none", padding: "7px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="idc-form-fields" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 16px" }}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>Full Name *</label>
                      <input value={form.name} onChange={setField("name")} placeholder="Enter full name" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12, textAlign: "center" }} />                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>ID Number</label>
                      <input value={form.idNo} onChange={setField("idNo")} placeholder="Auto-generated" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>
                        {type === "student" ? "Class - Section" : "Role / Designation"}
                      </label>
                      <input value={form.sub} onChange={setField("sub")} placeholder={type === "student" ? "e.g. Class 5 - A" : "e.g. Cleaner, Teacher, Admin"} style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>Phone</label>
                      <input value={form.phone} onChange={setField("phone")} placeholder="10-digit mobile" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>

                    {cfg.hasParent && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>
                          {type === "student" ? "Parent Name" : "Parent / Guardian Name"}
                        </label>
                        <input value={form.parentName} onChange={setField("parentName")} placeholder={type === "student" ? "Father / Mother name" : "Guardian name"} style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                      </div>
                    )}

                    <div style={{ marginBottom: 14, gridColumn: "1 / -1" }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>
                        Address
                      </label>
                      <input value={form.address} onChange={setField("address")} placeholder="House no, street, city, pincode" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>Blood Group</label>
                      <input value={form.bloodGroup} onChange={setField("bloodGroup")} placeholder="e.g. O+" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>Session</label>
                      <input value={form.session} onChange={setField("session")} placeholder="e.g. 2024-25" style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 10, color: "#2C5EAD", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase" }}>Valid Till</label>
                      <input type="date" value={form.validTill} onChange={setField("validTill")} style={{ width: "100%", border: "1px solid #C4E2F5", borderRadius: 10, padding: "9px 12px", fontSize: 12 }} />
                    </div>
                  </div>

                  <div className="idc-action-buttons" style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <button className="idc-btn" onClick={handlePrintSingle} style={{ flex: 1, background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: 12, borderRadius: 14, fontSize: 13, fontWeight: 700 }}>
                      <i className="fas fa-print" style={{ marginRight: 8 }}></i>Print
                    </button>
                    <button
                      className="idc-btn"
                      onClick={handleDownloadSingle}
                      disabled={downloading}
                      style={{ flex: 1, background: effectiveGradient, color: "#FFFFFF", border: "none", padding: 12, borderRadius: 14, fontSize: 13, fontWeight: 800, cursor: downloading ? "not-allowed" : "pointer", boxShadow: "0 10px 22px -8px rgba(13,71,161,0.5)" }}
                    >
                      {downloading ? <><i className="fas fa-spinner fa-pulse" style={{ marginRight: 8 }}></i>Generating...</> : <><i className="fas fa-download" style={{ marginRight: 8 }}></i>Download PDF</>}
                    </button>
                  </div>
                </div>

                {/* Live preview + printable area */}
                <div className="idc-preview-container" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: "#4A6D8C", fontWeight: 700, textTransform: "uppercase", marginBottom: 10, textAlign: "center" }}>Live Preview</div>
                  <div id="idc-print-area" style={{ display: "flex", justifyContent: "center" }}>
                    <div ref={cardRef}>
                      <IdCardFace type={type} data={form} shape={shape} color={effectiveColor} photoShape={photoShape} principalSignature={principalSignature} schoolLogo={schoolLogo} schoolAddress={schoolAddress} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ BULK MODE ══════════════ */}
          {mode === "bulk" && cfg.hasDb && (
            <div>
              {!bulkGenerated ? (
                <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid #C4E2F5", padding: 20, boxShadow: "0 4px 16px -8px rgba(13,36,54,0.1)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#2C5EAD" }}>
                      Select {cfg.label}s <span style={{ fontWeight: 400, fontSize: 11, color: "#4A6D8C" }}>({bulkSelected.size} selected)</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." style={{ border: "1px solid #C4E2F5", borderRadius: 10, padding: "7px 12px", fontSize: 12, maxWidth: 140 }} />
                      <button className="idc-btn" onClick={toggleSelectAll} style={{ background: "#FFFFFF", border: "1px solid #C4E2F5", padding: "7px 14px", borderRadius: 10, fontSize: 11, fontWeight: 700, color: "#2C5EAD" }}>
                        {bulkSelected.size === filteredDb.length && filteredDb.length > 0 ? "Deselect All" : "Select All"}
                      </button>
                    </div>
                  </div>

                  <div className="idc-bulk-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, maxHeight: 480, overflowY: "auto" }}>
                    {loadingDb ? (
                      <div style={{ textAlign: "center", padding: 30, color: "#4A6D8C", fontSize: 12 }}><i className="fas fa-spinner fa-pulse"></i> Loading...</div>
                    ) : filteredDb.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 30, color: "#4A6D8C", fontSize: 12 }}>No records found</div>
                    ) : (
                      filteredDb.map((rec) => {
                        const checked = bulkSelected.has(rec._id);
                        return (
                          <div
                            key={rec._id}
                            className="idc-row"
                            onClick={() => toggleBulk(rec._id)}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, border: `1px solid ${checked ? cfg.color : "#C4E2F5"}`, background: checked ? "#E3F2FD" : "#FFFFFF", cursor: "pointer" }}
                          >
                            <input type="checkbox" checked={checked} readOnly style={{ width: 14, height: 14 }} />
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: cfg.gradient, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                              {initials(rec.name)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#2C5EAD", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{rec.name}</div>
                              <div style={{ fontSize: 10, color: "#4A6D8C" }}>{rec.sub}</div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <button
                    className="idc-btn"
                    onClick={handleGenerateBulk}
                    style={{ marginTop: 16, width: "100%", background: effectiveGradient, color: "#FFFFFF", border: "none", padding: 13, borderRadius: 14, fontSize: 13, fontWeight: 800, boxShadow: "0 10px 22px -8px rgba(13,71,161,0.5)" }}
                  >
                    <i className="fas fa-magic" style={{ marginRight: 8 }}></i>Generate {bulkSelected.size || ""} ID Card{bulkSelected.size === 1 ? "" : "s"}
                  </button>
                  <div style={{ fontSize: 10.5, color: "#4A6D8C", marginTop: 8 }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                    Bulk cards use initial-avatars. For photo cards, use Single Card mode.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                    <button className="idc-btn" onClick={() => setBulkGenerated(false)} style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "10px 18px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                      <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i>Back to Selection
                    </button>
                    <button className="idc-btn" onClick={handlePrintBulk} style={{ background: "#FFFFFF", color: "#2C5EAD", border: "1px solid #C4E2F5", padding: "10px 18px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                      <i className="fas fa-print" style={{ marginRight: 6 }}></i>Print All
                    </button>
                    <button
                      className="idc-btn"
                      onClick={handleDownloadBulk}
                      disabled={downloading}
                      style={{ background: effectiveGradient, color: "#FFFFFF", border: "none", padding: "10px 18px", borderRadius: 12, fontSize: 12, fontWeight: 800, cursor: downloading ? "not-allowed" : "pointer", boxShadow: "0 10px 22px -8px rgba(13,71,161,0.5)" }}
                    >
                      {downloading ? <><i className="fas fa-spinner fa-pulse" style={{ marginRight: 6 }}></i>Generating...</> : <><i className="fas fa-download" style={{ marginRight: 6 }}></i>Download PDF ({selectedRecords.length})</>}
                    </button>
                  </div>

                  <div id="idc-print-area">
                    {bulkPages.map((pageRecords, pIdx) => (
                      <div
                        key={pIdx}
                        className="id-bulk-page"
                        style={{
                          background: "#FFFFFF",
                          padding: 20,
                          marginBottom: 20,
                          borderRadius: 16,
                          border: "1px solid #C4E2F5",
                          boxShadow: "0 4px 16px -8px rgba(13,36,54,0.1)",
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: 16,
                          justifyItems: "center",
                        }}
                      >
                        {pageRecords.map((rec) => (
                          <IdCardFace key={rec._id} type={type} data={rec} small shape={shape} color={effectiveColor} photoShape={photoShape} principalSignature={principalSignature} schoolLogo={schoolLogo} schoolAddress={schoolAddress} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}